import Link from 'next/link'
import { AutoplayOnView } from '@educator/components/AutoplayOnView'
import { Container } from '@educator/components/ProgramSections/Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '@educator/components/ProgramSections/i18n'
import type {
  HeroSectionData,
  ProgramSectionContext,
} from '@educator/components/ProgramSections/types/sections'
import { VideoControls } from '@educator/components/VideoControls'
import { useLandingEnrollment } from '@educator/hooks/useLandingEnrollment'
import { useResponsiveVideo } from '@educator/hooks/useResponsiveVideo'
import { useFireEvent } from '@educator/tracking/educatorTracking'
import { useTranslation } from '@mc/i18n'
import { Video } from '@mc/video'
import { HeroDetailBar } from './HeroDetailBar'
import { applyHeroDetails } from './applyHeroDetails'

interface HeroSectionProps extends ProgramSectionContext {
  data: HeroSectionData
}

export const HeroSection = ({
  data,
  initialIsMobile = false,
  initialCohortDetails,
}: HeroSectionProps) => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const { t: tEducator } = useTranslation('@mc/educator')
  const fireEvent = useFireEvent()

  const { heroStrip, primaryCta, isPrimaryLogIn } =
    useLandingEnrollment(initialCohortDetails)
  const { isMobile, videoUuid } = useResponsiveVideo({
    initialIsMobile,
    mobileUuid: data.video.mobileUuid,
    desktopUuid: data.video.desktopUuid,
  })

  const details = applyHeroDetails({
    details: data.details.map(({ labelKey, valueKey, kind }) => ({
      label: t(labelKey),
      value: t(valueKey),
      kind,
    })),
    heroStrip,
    t: tEducator,
  })

  const handleCtaClick = () => {
    if (!isPrimaryLogIn) {
      fireEvent('educatorPurchaseClick', {
        cta_location: 'hero',
        cta: primaryCta.label,
      })
    }
    fireEvent('educatorSignClick', {
      action: primaryCta.label.toLowerCase(),
      location: 'hero',
    })
  }

  return (
    <section data-section='hero' className='w-full pb-6 md:pb-12'>
      <Container className='md:bg-edu-bg-secondary'>
        {/* Copy over video until `xl`: tablet keeps the stacked mobile layout,
            the 624/540 two-column split is desktop-only. */}
        <div className='flex flex-col items-center gap-6 pt-12 pb-6 md:pb-12 xl:flex-row xl:gap-4 xl:pt-24 xl:pb-20'>
          <div className='flex w-full flex-col items-start gap-6 xl:basis-0 xl:grow-[624]'>
            <div className='flex flex-col gap-4 text-edu-fg-primary'>
              <h1 className='m-0 text-title-display-2 md:max-w-178 xl:max-w-none xl:text-title-display-1 xl:whitespace-pre-line'>
                {t(data.titleKey)}
              </h1>
              <p className='m-0 text-title-serif-4 font-serif font-normal italic md:max-w-160 xl:max-w-none xl:whitespace-pre-line'>
                {t(data.descriptionKey)}
              </p>
            </div>
            <Link
              href={primaryCta.href}
              onClick={handleCtaClick}
              data-element='hero-cta'
              className='bg-edu-brand-orange px-6 py-4 text-body-2-emphasis leading-none tracking-[0.8px] text-edu-fg-primary uppercase transition-colors duration-200 ease-out hover:bg-edu-orange-600'
            >
              {primaryCta.label}
            </Link>
          </div>
          <div
            className={`w-full overflow-hidden bg-edu-bg-tertiary md:aspect-video xl:basis-0 xl:grow-[540] ${isMobile ? 'aspect-square' : 'aspect-video'}`}
          >
            <Video
              mediaUuid={videoUuid}
              className='h-full w-full object-cover'
              playsInline
              fit='cover'
              autoPlay
              muted
              controls={false}
              loop
              exemptSingleton
            >
              <AutoplayOnView />
              <VideoControls />
            </Video>
          </div>
        </div>
        <HeroDetailBar
          details={details}
          partner={{
            label: t('hero.partnerLabel'),
            logoAlt: t('hero.partnerLogoAlt'),
          }}
        />
      </Container>
    </section>
  )
}
